
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserActiveCustomerPricingTierEntity from 'Models/Security/Acl/UserActiveCustomerPricingTierEntity';
import VisitorsActiveCustomerPricingTierEntity from 'Models/Security/Acl/VisitorsActiveCustomerPricingTierEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IActiveCustomerPricingTierEntityAttributes extends IModelAttributes {
	maximumCustomers: number;
	approvePrice: number;
	intelPrice: number;
	ppsrPrice: number;


}

@entity('ActiveCustomerPricingTierEntity', 'Active Customer Pricing Tier')
export default class ActiveCustomerPricingTierEntity extends Model
	implements IActiveCustomerPricingTierEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserActiveCustomerPricingTierEntity(),
		new VisitorsActiveCustomerPricingTierEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	/**
	 * Maximum Customers
	 */
	@Validators.Integer()
	@observable
	@attribute<ActiveCustomerPricingTierEntity, number>()
	@CRUD({
		name: 'Maximum Customers',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public maximumCustomers: number;
	

	
	/**
	 * Approve Price
	 */
	@Validators.Integer()
	@observable
	@attribute<ActiveCustomerPricingTierEntity, number>()
	@CRUD({
		name: 'Approve Price',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public approvePrice: number;
	

	
	/**
	 * Intel Price
	 */
	@Validators.Integer()
	@observable
	@attribute<ActiveCustomerPricingTierEntity, number>()
	@CRUD({
		name: 'Intel Price',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public intelPrice: number;
	

	
	/**
	 * PPSR Price
	 */
	@Validators.Integer()
	@observable
	@attribute<ActiveCustomerPricingTierEntity, number>()
	@CRUD({
		name: 'PPSR Price',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public ppsrPrice: number;


	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IActiveCustomerPricingTierEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IActiveCustomerPricingTierEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.maximumCustomers !== undefined) {
				this.maximumCustomers = attributes.maximumCustomers;
			}
			if (attributes.approvePrice !== undefined) {
				this.approvePrice = attributes.approvePrice;
			}
			if (attributes.intelPrice !== undefined) {
				this.intelPrice = attributes.intelPrice;
			}
			if (attributes.ppsrPrice !== undefined) {
				this.ppsrPrice = attributes.ppsrPrice;
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ActiveCustomerPricingTierEntity.prototype, 'created');
CRUD(modifiedAttr)(ActiveCustomerPricingTierEntity.prototype, 'modified');
